import {bootstrapApplication, createApplication} from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import {environment} from './environments/environment';
import {appConfig} from './app/app.config';
import {AppComponent} from './app/app.component';
import {createCustomElement} from '@angular/elements';
import {AppWebComponent} from './app/app.web.component';
import {registerLocaleData} from '@angular/common';
import localeIt from '@angular/common/locales/it';

if (environment.sentryDsn) {
  const currentEnv = (environment as any).sentryEnvironment || (environment.production ? 'production' : 'development');
  const appVersion = (environment as any).appVersion || environment.apiVersion || '1.0.1';
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: currentEnv,
    release: `giocalendario-frontend@${appVersion}`,
    ignoreErrors: [
      'Script error.',
      '[1Blocker]',
      'webkit-masked-url',
      'NG0913',
      'sanitizing HTML stripped some content'
    ],
    integrations: [
      Sentry.browserTracingIntegration({
        instrumentNavigation: true,
        instrumentPageLoad: true,
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false
      }),
      Sentry.captureConsoleIntegration({ levels: ['warn', 'error'] })
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'giocalendario-api-test.simonelambiase.it',
      'giocalendario-api-production.simonelambiase.it',
      /simonelambiase\.it/,
      /^\/api/
    ],
    replaysSessionSampleRate: currentEnv === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
          return null;
        }
      }

      const errorMsg = hint?.originalException?.toString() || event.message || '';
      if (errorMsg.includes('0 Unknown Error') && errorMsg.includes('Http failure response for')) {
        return null;
      }

      return event;
    },
    beforeSendTransaction(transaction) {
      if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
          return null;
        }
      }
      return transaction;
    }
  });

  const isLocal = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '[::1]');

  if (!isLocal) {
    // Emit initial structured log and metric to activate views in Sentry only in deployed environments
    Sentry.logger.info(`Giocalendario Frontend initialized in environment ${currentEnv} at release ${appVersion}`);
    Sentry.metrics.count('frontend.app.startup', 1);
    Sentry.metrics.gauge('frontend.session.ready', 1);
  }
}

// Bundle Bootstrap JS into main chunk so web component is self-contained.
// Also make it available globally for component code that accesses window.bootstrap.
import * as bootstrap from 'bootstrap';
(window as any).bootstrap = bootstrap;

registerLocaleData(localeIt);

(async () => {
  try {
    if (document.querySelector('app-root')) {
      await bootstrapApplication(AppComponent, appConfig);
    }
    const app = await createApplication(appConfig);
    const element = createCustomElement(AppWebComponent, {injector: app.injector});

    if (!customElements.get('giocalendario-table-widget')) {
      customElements.define('giocalendario-table-widget', element);
    }
  } catch (err) {
    console.error('Errore durante il bootstrap:', err);
  }
})();
